import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Loader2 } from "lucide-react";

// Secure attachments: private-by-default, signed temporary download URLs.
export default function FileUpload({ entityId, category = "crop_photo", label = "Attachments" }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const load = () => api.get(`/files?entity_id=${entityId}&category=${category}`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { if (entityId) load(); }, [entityId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("entity_id", entityId);
    try {
      await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const del = async (id) => { try { await api.delete(`/files/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="rounded-md border border-subtle bg-secondary/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-charcoal">{label}</span>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy || !entityId}
          className="inline-flex items-center gap-1 rounded-md border border-forest px-2 py-1 text-xs font-semibold text-forest hover:bg-forest/5 disabled:opacity-50" data-testid="file-upload-btn">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
        </button>
        <input ref={inputRef} type="file" className="hidden" accept="image/*,application/pdf,.csv,.txt" onChange={onPick} data-testid="file-input" />
      </div>
      {!entityId && <p className="text-xs text-mutedtext">Save the record first to attach files.</p>}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {files.map((f) => (
          <div key={f.id} className="group relative rounded border border-subtle bg-white p-1" data-testid={`file-${f.id}`}>
            {f.content_type?.startsWith("image/") ? (
              <img src={`${API.replace(/\/api$/, "")}${f.signed_url}`} alt={f.original_filename} className="h-16 w-full rounded object-cover" />
            ) : (
              <div className="flex h-16 items-center justify-center"><FileText className="h-6 w-6 text-mutedtext" /></div>
            )}
            <div className="mt-1 truncate text-[10px] text-mutedtext">{f.original_filename}</div>
            <button type="button" onClick={() => del(f.id)} className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-crittext opacity-0 group-hover:opacity-100" aria-label="Delete"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
